import {Box, Button, createStyles, getStylesRef, Tabs, UnstyledButton} from '@mantine/core';
import React, {useCallback} from 'react';

import {MAX_REACTION_CHEM_SEARCH_CLAUSES} from 'chemistry/ChemistryConstants';
import {ReactionSearchClauseDraft} from 'chemistry/ReactionChemSearchUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const REACTION_MOLECULE_TAB_HORIZONTAL_PADDING = 14;
const REACTION_MOLECULE_TAB_WIDTH = 120;
const REACTION_MOLECULE_TAB_MIN_WIDTH = 90;

export interface ReactionMoleculeTabsProps {
  clauses: Array<ReactionSearchClauseDraft>;
  activeClauseId: string;
  onSelectTab: (clauseId: string) => void;
  onAddClause: () => void;
  onRemoveClause: (clauseId: string) => void;
}

export default function ReactionMoleculeTabs({
  clauses,
  activeClauseId,
  onSelectTab,
  onAddClause,
  onRemoveClause,
}: ReactionMoleculeTabsProps): JSX.Element {
  const {classes, cx} = useStyles();
  const handleRemoveClause = useCallback(
    (clauseId: string): void => {
      const removedClauseIndex = clauses.findIndex((clause) => clause.id === clauseId);
      const wasActive = clauseId === activeClauseId;
      let nextFocusClauseId: string | null = null;
      if (wasActive) {
        const nextClauses = clauses.filter((clause) => clause.id !== clauseId);
        const successorIndex = Math.min(removedClauseIndex, nextClauses.length - 1);
        nextFocusClauseId = nextClauses[successorIndex]?.id ?? null;
      }
      onRemoveClause(clauseId);
      if (nextFocusClauseId != null) {
        requestAnimationFrame(() => {
          document.getElementById(`reaction_molecule_${nextFocusClauseId}_TAB`)?.focus();
        });
      }
    },
    [activeClauseId, clauses, onRemoveClause]
  );

  return (
    <Box className={classes.reactionMoleculeSelectorTabs}>
      <Tabs
        classNames={classes}
        value={activeClauseId}
        onTabChange={(value) => {
          if (value != null) {
            onSelectTab(value);
          }
        }}
      >
        <Tabs.List>
          {clauses.map((clause, index) => (
            <Box key={clause.id} className={classes.tabItem} role="presentation">
              <Tabs.Tab
                aria-controls={`reaction_molecule_${clause.id}_PANEL`}
                id={`reaction_molecule_${clause.id}_TAB`}
                value={clause.id}
              >
                <span
                  className={cx(
                    classes.moleculeTabText,
                    clauses.length > 1 && classes.moleculeTabTextWithClose
                  )}
                >
                  Molecule {index + 1}
                </span>
              </Tabs.Tab>
              {clauses.length > 1 ? (
                <UnstyledButton
                  aria-label={`Remove Molecule ${index + 1}`}
                  className={classes.closeMoleculeTab}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleRemoveClause(clause.id);
                  }}
                  onMouseDown={(event) => {
                    event.stopPropagation();
                  }}
                  type="button"
                  {...getTestProps({
                    component: 'ChemSearchModal',
                    element: `closeMoleculeTab-${index + 1}`,
                  })}
                >
                  <BenchlingIcon className={classes.closeMoleculeTabIcon} icon="close" />
                </UnstyledButton>
              ) : null}
            </Box>
          ))}
          {clauses.length < MAX_REACTION_CHEM_SEARCH_CLAUSES ? (
            <Button
              className={classes.addMoleculeTab}
              onClick={onAddClause}
              rightIcon={<BenchlingIcon className={classes.addMoleculeIcon} icon="plus" />}
              size="xs"
              type="button"
              variant="ghost"
            >
              Add molecule
            </Button>
          ) : null}
        </Tabs.List>
      </Tabs>
    </Box>
  );
}

const useStyles = createStyles((theme) => ({
  reactionMoleculeSelectorTabs: {
    boxShadow: `inset 0 -1px 0 ${theme.colors.gray[2]}`,
    paddingTop: theme.spacing.md,
  },
  tabsList: {
    borderBottom: 0,
    flexWrap: 'nowrap',
    overflow: 'visible',
    width: '100%',
  },
  tabItem: {
    [`&:hover .${getStylesRef('closeMoleculeTab')}, &:focus-within .${getStylesRef('closeMoleculeTab')}`]: {
      opacity: 1,
    },
    '&:hover, &:focus-within': {
      zIndex: 1,
    },
    alignItems: 'center',
    display: 'flex',
    flex: `0 1 ${REACTION_MOLECULE_TAB_WIDTH}px`,
    minWidth: REACTION_MOLECULE_TAB_MIN_WIDTH,
    position: 'relative',
  },
  tab: {
    borderBottom: '2px solid transparent',
    borderRadius: 0,
    color: theme.colors.gray[6],
    flex: 1,
    fontSize: theme.fontSizes.md,
    height: 36,
    minWidth: 0,
    padding: `0 ${REACTION_MOLECULE_TAB_HORIZONTAL_PADDING}px`,
    '&:hover': {
      borderBottomColor: theme.colors.gray[3],
    },
    '&[data-active]': {
      borderBottomColor: theme.colors.blue[8],
      color: theme.colors.blue[8],
    },
  },
  addMoleculeTab: {
    backgroundColor: 'transparent',
    borderBottom: '2px solid transparent',
    borderRadius: 0,
    color: theme.colors.blue[8],
    fontSize: theme.fontSizes.md,
    flexShrink: 0,
    height: 36,
    // Overlap the tab-strip border like Mantine Tabs.Tab.
    marginBottom: -2,
    padding: `0 ${theme.spacing.lg}`,
    whiteSpace: 'nowrap',
    ':active': {
      transform: 'none',
    },
    ':hover': {
      backgroundColor: theme.colors.gray[0],
      borderBottomColor: theme.colors.gray[3],
    },
  },
  addMoleculeIcon: {
    height: 12,
    width: 12,
  },
  moleculeTabText: {
    display: 'block',
    minWidth: 0,
    overflow: 'hidden',
    textAlign: 'center',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  moleculeTabTextWithClose: {
    paddingRight: 10,
  },
  closeMoleculeTab: {
    ref: getStylesRef('closeMoleculeTab'),
    alignItems: 'center',
    background: 'none',
    border: 0,
    borderRadius: 2,
    boxSizing: 'border-box',
    color: theme.colors.gray[3],
    cursor: 'pointer',
    display: 'flex',
    height: 18,
    opacity: 0,
    padding: 3,
    position: 'absolute',
    right: 5,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 18,
    zIndex: 1,
    ':hover': {
      color: theme.colors.gray[7],
    },
    ':focus-visible': {
      opacity: 1,
    },
  },
  closeMoleculeTabIcon: {
    color: 'inherit',
    fontSize: 12,
    height: 12,
    width: 12,
  },
}));
